<script lang="ts">
	import type { EmblaCarouselType } from "embla-carousel";
	import Autoplay from "embla-carousel-autoplay";
	import { getAboutPath } from "@core/schema/paths/getAboutPath.js";
	import computer from "../assets/photos/computer.png?webp&w=620&imagetools";
	import globe from "../assets/photos/globe.png?webp&w=620&imagetools";
	import history from "../assets/photos/history.png?webp&w=620&imagetools";
	import key from "../assets/photos/key.png?webp&w=620&imagetools";
	import service from "../assets/photos/service.png?webp&w=620&imagetools";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./WhyCooperate.translations.json" with { type: "json" };
	import WhyItem from "./WhyItem.svelte";
	import Carousel from "./common/Carousel.svelte";
	import Link from "./common/Link.svelte";
	import Media from "./common/Media.svelte";
	import TextWithIcon from "./common/TextWithIcon.svelte";
	import { ButtonVariant } from "./common/getButtonClass.js";

	const seconds = 8;

	const { tenant } = getGlobalTemplateProps();

	const translate = getTranslate(translations);

	interface Slide {
		title: string;
		subtitle: string;
		image: string;
	}

	const slides: Slide[] = [
		{
			title: translate("title1"),
			subtitle: translate("title1text"),
			image: key,
		},
		{
			title: translate("title2"),
			subtitle: translate("title2text"),
			image: computer,
		},
		{
			title: translate("title3"),
			subtitle: translate("title3text"),
			image: globe,
		},
		{
			title: translate("title4"),
			subtitle: translate("title4text"),
			image: service,
		},
		{
			title: translate("title5"),
			subtitle: translate("title5text"),
			image: history,
		},
	];

	let carouselApi: EmblaCarouselType | undefined = $state();
	let selectedIndex = $state(0);

	// eslint-disable-next-line new-cap
	const autoplayPlugin = Autoplay({ delay: seconds * 1000, stopOnInteraction: true, stopOnMouseEnter: false });

	function goToSlide(index: number): void {
		carouselApi?.scrollTo(index);
	}

	$effect(() => {
		if (carouselApi) {
			const api = carouselApi;

			const onSelect = (): void => {
				selectedIndex = api.selectedScrollSnap();
			};

			api.on("select", onSelect);

			return (): void => {
				api.off("select", onSelect);
			};
		}

		return undefined;
	});
</script>

<section class="full-width bg-zms-grey grid-container">
	<div class="container-center flex items-center flex-col gap-8 sm:gap-12 md:gap-16 lg:gap-[5.5rem]">
		<h2 class="text-left md:text-center w-full">{translate("title")}</h2>
		<div class="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-6 w-full">
			<div class="flex flex-col gap-4 sm:gap-6 md:gap-12">
				{#each slides as item, i (i)}
					<button
						class="text-left outline-none"
						onclick={(): void => {
							goToSlide(i);
						}}
					>
						<WhyItem
							number={i + 1}
							title={item.title}
							subtitle={item.subtitle}
							isActive={i === selectedIndex}
							duration={seconds}
						/>
					</button>
				{/each}
			</div>
			<div class="hidden md:block overflow-hidden">
				<Carousel
					bind:api={carouselApi}
					items={slides}
					type="flex"
					plugins={[autoplayPlugin]}
					options={{
						loop: true,
						align: "center",
						slidesToScroll: 1,
						containScroll: "trimSnaps",
						watchDrag: false,
					}}
				>
					{#snippet item(slide: Slide)}
						<div class="flex-none w-full h-[560px] relative">
							<Media
								class="w-full h-full object-contain"
								height={560}
								width={584}
								alt={slide.title}
								source={{ src: slide.image, type: "image/png" }}
							/>
						</div>
					{/snippet}
				</Carousel>
			</div>
		</div>
	</div>
	<div class="mt-12 sm:mt-14 md:mt-16 flex w-full justify-start md:justify-center">
		<Link href={getAboutPath(tenant)} variant={ButtonVariant.OutlinedWithIcon}
			><TextWithIcon>{translate("moreAboutUs")}</TextWithIcon></Link
		>
	</div>
</section>
